import type { UseFormSetError } from 'react-hook-form';

import { ValidationException } from '@/core/exceptions';
import { useToast } from '@/shared/components/toasts';

import { mapValidationErrors, setServerErrors } from './serverErrors';

/**
 * Shared handler for server-side form errors (ValidationException + generic errors).
 *
 * Keeps form components small and consistent across modules.
 */
export function useServerFormErrors(
  fieldMap: Record<string, string>,
  fallbackMessage: string
) {
  const toast = useToast();

  return (error: unknown, setError: UseFormSetError<any>) => {
    if (error instanceof ValidationException) {
      const fallbackMessageText =
        error.userMessage || error.message || fallbackMessage;

      // 1) Map validationErrors (400/422 body.errors or body.validationErrors)
      const result = mapValidationErrors(
        error.validationErrors,
        fieldMap,
        fallbackMessageText
      );

      if (result.hasMappedErrors) {
        setServerErrors(setError, result.mappedErrors);
      }

      if (result.hasUnmatchedErrors) {
        toast.error(result.unmatchedErrors.join('. '));
      }

      // 2) Single-field 400 (e.g. body.field + body.message without validationErrors)
      if (
        !result.hasMappedErrors &&
        error.field &&
        fieldMap[error.field]
      ) {
        setError(fieldMap[error.field], {
          type: 'server',
          message: fallbackMessageText,
        });
        return;
      }

      if (!result.hasMappedErrors && !result.hasUnmatchedErrors) {
        toast.error(fallbackMessageText);
      }

      return;
    }

    const message = error instanceof Error ? error.message : fallbackMessage;
    toast.error(message);
  };
}


